import { cn } from "@/utils/cn";
import { Badge } from "@babackapp/ui/components/badge";
import {
  SidebarMenuButton,
  SidebarMenuItem,
} from "@babackapp/ui/components/sidebar";
import {
  ExclamationTriangleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/solid";
import React from "react";
import { Link, useLocation } from "react-router-dom";

type NavigationSectionItemProps = {
  to?: string;
  icon?: React.ReactNode;
  label: string;
  badge?: string;
  locked?: boolean;
  /** Shows a warning icon, with this text as its tooltip. */
  warning?: string;
  /** Pulses the warning icon: something is going wrong right now. */
  isWarningUrgent?: boolean;
};

export function NavigationSectionItem({
  to,
  icon,
  label,
  badge,
  locked,
  warning,
  isWarningUrgent,
}: NavigationSectionItemProps) {
  const { pathname } = useLocation();

  const selected = pathname === to;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={selected}>
        <Link to={to ?? "#"} className={locked ? "opacity-60" : undefined}>
          {icon}
          <span>{label}</span>
          {locked ? (
            <LockClosedIcon className="ml-auto h-3 w-3 text-muted-foreground" />
          ) : warning ? (
            <span
              className="ml-auto"
              role="img"
              title={warning}
              aria-label={warning}
            >
              <ExclamationTriangleIcon
                className={cn(
                  "h-4 w-4 text-amber-500",
                  isWarningUrgent && "motion-safe:animate-pulse",
                )}
              />
            </span>
          ) : badge ? (
            <Badge
              variant="default"
              size="xs"
              className="ml-auto rounded-full border-0 bg-gradient-to-r from-indigo-500 to-purple-500 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm"
            >
              {badge}
            </Badge>
          ) : null}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
